import React, { useState, useEffect } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom"; // ✅ ADDED for routing
import logo from "./assets/mealno-brand-logo.svg";
import iconlogo from "./assets/App-icon.svg"; 
import { sendContactMessage } from "./api/api";
import { fetchIP } from "./utils/fetchIP";

// Import your team photos here
// import arnabPhoto from "./assets/arnab.png";
// import subhamPhoto from "./assets/founder.jpg";
// import subhajitPhoto from "./assets/subha.png";

// Import both videos
import foodVideoDesktop from "./assets/AllFood.mp4";
import foodVideoMobile from "./assets/AllFoodMobile.mp4";

import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
// import XIcon from '@mui/icons-material/X';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

// ─── GOLDEN SAFFRON THEME ─────────────────────────────────────────────────────
const T = {
    bg: "#FFFFE0",
    card: "#FFFFFF",
    gold: "#FFD700",
    goldDk: "#FFC107",
    brown: "#6D4C41",
    brownLt: "#A1887F",
    red: "#FF4500",
    green: "#2E7D32",
    border: "rgba(109,76,65,0.12)",
    shadow: "rgba(109,76,65,0.10)",
    gradBtn: "linear-gradient(135deg,#FFD700 0%,#FFC107 100%)",
    gradHero: "linear-gradient(135deg,#FFF8DC 0%,#FFFFE0 55%,#FFF3CD 100%)",
};

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const GlobalStyles = () => (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=Inter:wght@300;400;500;600;700&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
    html{scroll-behavior:smooth}
    body{background:${T.bg};color:${T.brown};font-family:'Inter',sans-serif;overflow-x:hidden}
    ::-webkit-scrollbar{width:5px}
    ::-webkit-scrollbar-track{background:#FFF8DC}
    ::-webkit-scrollbar-thumb{background:${T.gold};border-radius:3px}
    .dp{font-family:'Playfair Display',serif}

    @keyframes drift{
      0%{transform:translateX(0)translateY(0)scale(1);opacity:.55}
      50%{transform:translateX(15px)translateY(-30px)scale(1.1);opacity:.8}
      100%{transform:translateX(-10px)translateY(-60px)scale(.7);opacity:0}
    }
    @keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeText{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
    @keyframes shimmerGold{0%{background-position:-200% center}100%{background-position:200% center}}
    @keyframes pulseRing{0%{transform:scale(1);opacity:.5}100%{transform:scale(1.7);opacity:0}}
    @keyframes glowPulse{0%,100%{box-shadow:0 0 12px rgba(255,215,0,.35)}50%{box-shadow:0 0 24px rgba(255,215,0,.7)}}
    @keyframes floatBubble{0%,100%{transform:translateY(0) rotate(-1deg)}50%{transform:translateY(-8px) rotate(1deg)}}
    @keyframes bounceIn{0%{transform:scale(.7);opacity:0}60%{transform:scale(1.05);opacity:1}100%{transform:scale(1)}}
    @keyframes rotateSlow{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
    @keyframes radarPing{0%{transform:scale(.5);opacity:.8}100%{transform:scale(2.2);opacity:0}}
    @keyframes scooterRun{0%{left:0%;opacity:1}90%{opacity:1}100%{left:85%;opacity:0}}
    @keyframes tickerScroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}

    .hamburger{display:none;flex-direction:column;gap:4px;cursor:pointer;padding:4px}
    .hamburger span{display:block;width:22px;height:2px;background:${T.brown};border-radius:2px;transition:all .3s}
    .mobile-menu{display:none;position:fixed;top:56px;left:0;right:0;z-index:99;
      background:rgba(255,255,224,.98);backdrop-filter:blur(12px);
      border-bottom:1px solid ${T.border};padding:16px 5%;flex-direction:column;gap:12px}
    .mobile-menu.open{display:flex}
    .mobile-menu a{color:${T.brown};text-decoration:none;font-size:14px;font-weight:600;
      padding:8px 0;border-bottom:1px solid ${T.border}}

    @media(max-width:900px){
      .hamburger{display:flex!important}
      .desktop-links{display:none!important}
      .two-col{flex-direction:column!important}
      .two-col > *{flex:unset!important;width:100%!important;min-width:unset!important}
      .footer-inner{flex-direction:column!important;gap:24px!important}
    }
    @media(max-width:580px){
      .hero-btns{flex-direction:column;align-items:center}
      .stats-flex{flex-wrap:wrap;gap:16px!important}
      .form-2col{grid-template-columns:1fr!important}
      .social-icons-grid{grid-template-columns:repeat(4,1fr)!important}
    }
  `}</style>
);
// ─── NAV ──────────────────────────────────────────────────────────────────────
const Nav = ({ active, setShowOrderPopup }) => {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", fn);
        return () => window.removeEventListener("scroll", fn);
    }, []);

    // Added "Policies" to the links array, Commented out "Team"
    const links = ["Home", "About", "How It Works", "Zones", /* "Team", */ "Contact", "Policies"];

    return (
        <>
            <Box component="nav" sx={{
                position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
                padding: { xs: "0 5%", md: "0 6%" }, height: { xs: 56, md: 64 },
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: scrolled ? "rgba(255,255,224,.97)" : "rgba(255,255,224,.85)",
                backdropFilter: "blur(18px)",
                borderBottom: scrolled ? `1px solid ${T.border}` : "1px solid transparent",
                boxShadow: scrolled ? `0 4px 16px ${T.shadow}` : "none",
                transition: "all .35s ease",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <Box component="img" src={logo} alt="Mealno Logo"
                        sx={{
                            height: { xs: 36, md: 44 }, width: "auto", objectFit: "contain",

                        }} />
                </Box>

                <Box className="desktop-links" sx={{ display: "flex", gap: { xs: "20px", md: "32px" }, alignItems: "center" }}>
                    {links.map(l => {
                        const id = l.toLowerCase().replace(/\s+/g, "-");
                        return (
                            <Box component="a" key={l} href={`#${id}`} sx={{
                                color: active === id ? "#4a342e" : T.brownLt,
                                textDecoration: "none", fontSize: { xs: 12, md: 14 }, fontWeight: 700,
                                letterSpacing: ".04em", textTransform: "uppercase",
                                borderBottom: active === id ? `2px solid ${"#4a342e"}` : "2px solid transparent",
                                paddingBottom: "2px", transition: "all .2s",
                                '&:hover': { color: "#4a342e" }
                            }}>
                                {l}
                            </Box>
                        );
                    })}
                </Box>

                <Box className="hamburger" onClick={() => setMenuOpen(o => !o)}>
                    <span style={{ transform: menuOpen ? "rotate(45deg) translateY(6px)" : "none" }} />
                    <span style={{ opacity: menuOpen ? 0 : 1 }} />
                    <span style={{ transform: menuOpen ? "rotate(-45deg) translateY(-6px)" : "none" }} />
                </Box>
            </Box>

            <Box className={`mobile-menu${menuOpen ? " open" : ""}`}>
                {links.map(l => (
                    <a key={l} href={`#${l.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setMenuOpen(false)}>{l}</a>
                ))}
            </Box>
        </>
    );
};

// ─── TICKER BANNER ────────────────────────────────────────────────────────────
const Ticker = () => {
    const items = ["🍛 Veg Thali", "🍗 Chicken Thali", "🐟 Fish Curry + Rice", "🍳 Fried Rice", "🫓 Roti + Dal + Sabji", "🍚 Dal Rice", "🍗 Chicken Chap", "🥘 Special Biryani", "🍗 Chicken Kosa", "🐟 Fish Fry"];
    const repeated = [...items, ...items];
    return (
        <Box sx={{ background: "#4a342e", overflow: "hidden", padding: { xs: "8px 0", md: "12px 0" }, borderBottom: `2px solid ${T.gold}` }}>
            <Box sx={{ display: "flex", gap: { xs: "36px", md: "60px" }, whiteSpace: "nowrap", animation: "tickerScroll 35s linear infinite", width: "max-content" }}>
                {repeated.map((item, i) => (
                    <Typography component="span" key={i} sx={{ fontSize: { xs: 12, md: 14 }, fontWeight: 700, color: T.gold, letterSpacing: ".02em", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        {item}<span style={{ color: "rgba(255,215,0,.35)", marginLeft: { xs: 18, md: 30 } }}>◆</span>
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

// ─── HOME ─────────────────────────────────────────────────────────────────────
const HERO_TEXTS = [
    { line1: "Ghar Jaisa Khana,", line2: "Pocket-Friendly Daam." },
    { line1: "Hot & Fresh Meals,", line2: "Delivered in 30 Mins." },
    { line1: "Zero Delivery Fees,", line2: "Within Your 2km Zone." },
    { line1: "Daily Changing Menu,", line2: "Just Like Home." }
];

const Home = ({ setShowOrderPopup }) => {
    // DYNAMIC TEXT LOGIC
    const [textIndex, setTextIndex] = useState(0);

    // RESPONSIVE VIDEO LOGIC
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // ─── COUNTDOWN LOGIC ───
    const calculateTimeLeft = () => {
        // Target Date: August 15, 2026 at Midnight IST
        // +05:30 ensures it launches at India time, regardless of the user's device timezone
        const difference = +new Date("2026-08-15T00:00:00+05:30") - +new Date();
        let timeLeft = {};

        if (difference > 0) {
            timeLeft = {
                Days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                Hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                Mins: Math.floor((difference / 1000 / 60) % 60),
                Secs: Math.floor((difference / 1000) % 60)
            };
        }
        return timeLeft;
    };

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

    useEffect(() => {
        // setInterval with [] ensures this effect only mounts once, improving performance
        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
    }, []); // <-- This empty array is critical

    useEffect(() => {
        const timer = setInterval(() => {
            setTextIndex((prevIndex) => (prevIndex + 1) % HERO_TEXTS.length);
        }, 4000);

        return () => clearInterval(timer);
    }, []);

    return (
        <Box component="section" id="home" sx={{
            scrollMarginTop: "90px",
            minHeight: { xs: "90vh", md: "100vh" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: { xs: "40px 5% 40px", md: "30px 5% 60px" },
            overflow: "hidden",
            position: "relative"
        }}>

            {/* ─── FULL BACKGROUND VIDEO ─── */}
            <video
                key={isMobile ? 'mobile' : 'desktop'}
                autoPlay
                loop
                muted={true}
                playsInline
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    zIndex: 0
                }}
            >
                <source src={isMobile ? foodVideoMobile : foodVideoDesktop} type="video/mp4" />
            </video>

            {/* OVERLAY FOR TEXT READABILITY */}
            <Box sx={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(135deg,rgb(255, 255, 224) 0%, transparent 100%)",
                zIndex: 1
            }} />

            {/* ─── MAIN TEXT CONTENT (Centered over video) ─── */}
            <Box sx={{ position: "relative", zIndex: 2, maxWidth: { xs: 700, md: 850 }, width: "100%" }}>

                <Box sx={{ display: "inline-flex", alignItems: "center", gap: { xs: "6px", md: "10px" }, padding: { xs: "6px 16px", md: "8px 20px" }, borderRadius: "24px", background: "rgba(255,215,0,.3)", border: `1.5px solid ${T.gold}`, fontSize: { xs: 11, md: 13 }, letterSpacing: ".08em", textTransform: "uppercase", color: "#4a342e", fontWeight: 700, marginBottom: "2px", animation: "fadeUp .6s ease both", backdropFilter: "blur(4px)" }}>
                    <Box component="span" sx={{ width: { xs: 6, md: 8 }, height: { xs: 6, md: 8 }, borderRadius: "50%", background: T.green, boxShadow: `0 0 0 3px rgba(46,125,50,.25)`, animation: "glowPulse 2s ease infinite" }} />
                    Now Delivering in Your Zone · Within 2 km
                </Box>

                {/* DYNAMIC TYPOGRAPHY */}
                <Box sx={{ minHeight: { xs: "100px", md: "140px" }, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <Typography key={textIndex} component="h1" className="dp" sx={{ fontSize: "clamp(1.8rem, 5vw, 4.5rem)", lineHeight: 1.1, fontWeight: 900, color: "#4a342e", marginBottom: "5px", animation: "fadeText 0.5s ease both", textShadow: "0 4px 16px rgba(255,255,255,0.7)" }}>
                        {HERO_TEXTS[textIndex].line1}<br />
                        <Box component="span" sx={{ background: "linear-gradient(90deg, #fa6800, #fd6a0e, #f25d00)", backgroundSize: "200% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmerGold 3s linear infinite", display: "inline-block", textShadow: "none" }}>
                            {HERO_TEXTS[textIndex].line2}
                        </Box>
                    </Typography>
                </Box>

                <Typography sx={{ fontSize: { xs: 14, md: 18 }, color: "#4a342e", fontWeight: 600, lineHeight: 1.7, maxWidth: { xs: 480, md: 640 }, margin: "0 auto 16px", animation: "fadeUp .7s .2s ease both", opacity: 0, animationFillMode: "forwards", textShadow: "0 2px 8px rgba(255,255,255,0.6)" }}>
                    Fresh daily meals — Biryani, Veg Thali, Fish Thali, combos — delivered hot from our <strong>zone kitchen</strong> to your door within <strong style={{ color: T.red }}>2 km</strong>.
                </Typography>

                <Box sx={{ display: "inline-block", padding: { xs: "6px 16px", md: "10px 24px" }, borderRadius: "24px", background: `rgba(255,255,255,.6)`, border: `1.5px solid rgba(255,69,0,.25)`, fontSize: { xs: 13, md: 15 }, fontWeight: 700, color: T.red, marginBottom: "26px", animation: "fadeUp .7s .25s ease both", opacity: 0, animationFillMode: "forwards", backdropFilter: "blur(4px)" }}>
                    Meals starting at just ₹50 · No delivery fee in zone
                </Box>

                <Box className="hero-btns" sx={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap", animation: "fadeUp .7s .3s ease both", opacity: 0, animationFillMode: "forwards" }}>
                    <Box component="button" onClick={() => setShowOrderPopup(true)} sx={{
                        fontFamily: "inherit",
                        textDecoration: 'none', padding: { xs: "12px 28px", md: "14px 36px" },
                        borderRadius: "40px", background: T.gradBtn, color: "#4a342e",
                        fontWeight: 800, fontSize: { xs: 14, md: 15 }, border: "1px solid black",
                        cursor: "pointer", boxShadow: `0 6px 20px rgba(255,215,0,.4)`,
                        transition: "transform .2s,box-shadow .2s",
                        '&:hover': { background: "#4a342e", color: T.bg }
                    }}>
                        Order Now
                    </Box>
                    <Box component="a" href="https://user.mealno.in/menu" sx={{ textDecoration: 'none', padding: { xs: "12px 24px", md: "14px 32px" }, borderRadius: "40px", background: "rgba(255,255,255,0.6)", color: "#4a342e", fontWeight: 700, fontSize: { xs: 14, md: 15 }, border: `2px solid ${"#4a342e"}`, cursor: "pointer", transition: "all .2s", backdropFilter: "blur(4px)", '&:hover': { background: "#4a342e", color: T.bg } }}>
                        See Today's Menu
                    </Box>
                </Box>

                <Box className="stats-flex" sx={{
                    display: "flex",
                    gap: { xs: "16px", md: "24px" },
                    justifyContent: "center",
                    flexWrap: "wrap",
                    marginTop: { xs: "4px", md: "10px" },
                    borderTop: `1px solid rgba(109,76,65,0.15)`,
                    paddingTop: { xs: "28px", md: "36px" },
                    animation: "fadeUp .7s .45s ease both",
                    opacity: 0,
                    animationFillMode: "forwards"
                }}>
                    {[
                        { val: "₹50", label: "Meals Start At", icon: "💰" },
                        { val: "<30min", label: "Delivery Time", icon: "⚡" },
                        { val: "2 km", label: "Delivery Zone", icon: "📍" },
                        { val: "Daily", label: "Fresh Cooked", icon: "🔥" },
                    ].map(s => (
                        <Box key={s.label} sx={{
                            textAlign: "center",
                            background: "rgba(255, 255, 255, 0.45)",
                            border: "1px solid rgba(255, 255, 255, 0.8)",
                            borderRadius: "20px",
                            padding: { xs: "12px 16px", md: "16px 28px" },
                            boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                            minWidth: { xs: "130px", md: "160px" },
                            flex: { xs: "1 1 40%", sm: "0 1 auto" },
                            transition: "transform 0.2s",
                            '&:hover': { transform: "translateY(-4px)" }
                        }}>
                            <Box sx={{ fontSize: { xs: 18, md: 24 }, marginBottom: "6px" }}>{s.icon}</Box>
                            <Typography className="dp" sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 900, color: T.red, textShadow: "0 2px 6px rgba(239, 234, 234, 0.6)" }}>
                                {s.val}
                            </Typography>
                            <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#4a342e", marginTop: "4px", letterSpacing: ".04em", textTransform: "uppercase", fontWeight: 800 }}>
                                {s.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

// ─── ABOUT ────────────────────────────────────────────────────────────────────
const About = () => {
    const [activeTab, setActiveTab] = useState("story");
    const features = [
        { icon: "🤝", title: "Community Marketplace", desc: "We connect the best independent, local zone kitchens directly to you.", color: T.red },
        { icon: "📍", title: "Hyper-Local Focus", desc: "By keeping delivery radiuses tight (under 2 km), we ensure food is always hot.", color: "#E65100" },
        { icon: "👨‍🍳", title: "Empowering Local Cooks", desc: "Our platform gives local food entrepreneurs the network they need to scale.", color: T.gold },
        { icon: "📱", title: "Seamless Aggregation", desc: "One unified app to browse, order, and track.", color: "#1565C0" },
    ];
    const roadmap = [
        { phase: "Phase 1", title: "Concept & Infrastructure", status: "Completed ✅", desc: "Developed the core Mealno marketplace technology." },
        { phase: "Phase 2", title: "Alpha Testing", status: "In Progress 🚀", desc: "Currently running live Alpha tests in select zones." },
        { phase: "Phase 3", title: "Public Beta Launch", status: "Up Next ⏳", desc: "Opening the platform build the application to the wider local public." },
    ];

    return (
        <Box component="section" id="about" sx={{ scrollMarginTop: "60px", padding: { xs: "40px 5%", md: "60px 5%" }, background: "#FFF8E1" }}>
            <Box sx={{ maxWidth: 1100, margin: "0 auto" }}>
                <Box sx={{ textAlign: "center", marginBottom: { xs: "48px", md: "60px" } }}>
                    <Typography sx={{ fontSize: { xs: 11, md: 13 }, letterSpacing: ".1em", textTransform: "uppercase", color: T.red, fontWeight: 700, marginBottom: "12px" }}>— About Mealno</Typography>
                    <Typography component="h2" className="dp" sx={{ fontSize: "clamp(1.8rem, 3.5vw, 3.5rem)", fontWeight: 900, color: "#4a342e", lineHeight: 1.1 }}>
                        A new kind of food network,<br /><Box component="em" sx={{ color: T.red, fontStyle: "italic" }}>built for our community.</Box>
                    </Typography>
                    <Typography sx={{ color: T.brownLt, maxWidth: 680, margin: "16px auto 0", lineHeight: 1.7, fontSize: { xs: 14, md: 16 } }}>
                        We believe that accessing a hot, homestyle meal should be the easiest part of your day. By empowering local kitchens and restricting delivery radiuses to 2 km, we are creating a sustainable, community-first food network where quality goes up, and prices stay down.
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: { xs: "10px", md: "14px" }, justifyContent: "center", marginBottom: { xs: "40px", md: "56px" }, flexWrap: "wrap" }}>
                    {[{ key: "story", label: "Our Story" }, { key: "model", label: "Our Model" }, { key: "roadmap", label: "The Roadmap" }].map(tab => (
                        <Box component="button" key={tab.key} onClick={() => setActiveTab(tab.key)} sx={{
                            padding: { xs: "8px 18px", md: "10px 24px" }, borderRadius: "24px", background: activeTab === tab.key ? T.gradBtn : T.card,
                            color: activeTab === tab.key ? "#4a342e" : T.brownLt, border: `1px solid ${activeTab === tab.key ? T.gold : T.border}`,
                            fontWeight: 700, fontSize: { xs: 12, md: 14 }, cursor: "pointer",
                            boxShadow: activeTab === tab.key ? `0 4px 10px rgba(255,215,0,0.3)` : "none", transition: "all 0.2s",
                            '&:hover': { opacity: 0.9 }
                        }}>{tab.label}</Box>
                    ))}
                </Box>

                {activeTab === "story" && (
                    <Box className="about-top" sx={{ display: "flex", gap: "6%", alignItems: "center", animation: "fadeUp 0.4s ease both", flexWrap: "wrap" }}>
                        <Box sx={{ flex: 1, minWidth: 260 }}>
                            <Typography component="h3" className="dp" sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 700, color: "#4a342e", marginBottom: "16px" }}>Great food is local. <br />Delivery should be too.</Typography>
                            <Typography sx={{ color: T.brownLt, lineHeight: 1.8, fontSize: { xs: 14, md: 15 }, marginBottom: "16px" }}>Mealno was born out of a simple, everyday frustration. Why does ordering a comforting, standard daily meal—like a hot plate of Dal-Rice or a fresh Chicken Thali—cost so much in delivery fees? And why does it always take so long to arrive?</Typography>
                            <Typography sx={{ color: T.brownLt, lineHeight: 1.8, fontSize: { xs: 14, md: 15 }, marginBottom: "24px" }}>We realized that traditional food delivery apps rely on a broken, long-distance model. We decided to flip the script. Instead of connecting you to a restaurant 10 kilometers away, we find the absolute best independent kitchens within a strict 2-kilometer zone of your location and connect them directly to you. <br /><br />No traffic delays, no cold food, and no hidden markups. Just honest, pocket-friendly meals delivered to your door in under 30 minutes.

                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                {["🔥 Alpha Live", "⚡ Hyper-Local", "🤝 Aggregator"].map(v => (
                                    <Typography component="span" key={v} sx={{ padding: "6px 14px", borderRadius: "20px", background: `rgba(255,215,0,0.15)`, border: `1px solid ${T.gold}`, fontSize: 12, fontWeight: 600, color: "#4a342e" }}>{v}</Typography>
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{ flex: { xs: "0 0 100%", md: "0 0 45%" }, marginTop: { xs: "32px", md: 0 } }}>
                            <Box sx={{ background: T.card, borderRadius: "20px", padding: { xs: "28px", md: "40px" }, border: `1px solid ${T.border}`, boxShadow: `0 8px 24px ${T.shadow}`, textAlign: "center", animation: "floatBubble 4s ease-in-out infinite" }}>
                                <Box sx={{ fontSize: { xs: 40, md: 56 }, marginBottom: "12px" }}>🌱</Box>
                                <Typography component="h4" sx={{ fontSize: { xs: 16, md: 20 }, fontWeight: 800, color: "#4a342e", marginBottom: "6px" }}>Growing from the ground up.</Typography>
                                <Typography sx={{ fontSize: { xs: 13, md: 14 }, color: T.brownLt, fontWeight: 500 }}>We are proudly independent and focused on getting the details right.</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {activeTab === "model" && (
                    <Box sx={{ animation: "fadeUp 0.4s ease both" }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "24px" }}>
                            {features.map((f, i) => (
                                <Box key={f.title} sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "16px", padding: "24px", boxShadow: `0 4px 16px ${T.shadow}`, animation: `fadeUp 0.5s ${i * 0.06}s ease both` }}>
                                    <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: `${f.color}18`, border: `1px solid ${f.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, marginBottom: "14px" }}>{f.icon}</Box>
                                    <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, color: "#4a342e", marginBottom: "8px" }}>{f.title}</Typography>
                                    <Typography sx={{ fontSize: 14, color: T.brownLt, lineHeight: 1.7 }}>{f.desc}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {activeTab === "roadmap" && (
                    <Box sx={{ animation: "fadeUp 0.4s ease both", maxWidth: 800, margin: "0 auto" }}>
                        {roadmap.map((item, i) => (
                            <Box key={item.phase} sx={{ display: "flex", gap: "24px", marginBottom: "24px", alignItems: "flex-start", animation: `fadeUp 0.4s ${i * 0.1}s ease both` }}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                                    <Box sx={{ width: 56, height: 56, borderRadius: "50%", background: item.status.includes("Completed") ? T.green : item.status.includes("Progress") ? T.gradBtn : T.card, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: item.status.includes("Completed") ? "#fff" : "#4a342e", border: `2px solid ${T.border}`, textAlign: 'center' }}>{item.phase}</Box>
                                    {i < roadmap.length - 1 && <Box sx={{ width: 2, flex: 1, minHeight: 30, background: T.border, marginTop: "6px" }} />}
                                </Box>
                                <Box sx={{ background: T.card, borderRadius: "14px", padding: "20px 24px", border: `1px solid ${T.border}`, flex: 1, boxShadow: `0 3px 12px ${T.shadow}` }}>
                                    <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, color: "#4a342e", marginBottom: "6px" }}>{item.title} - <span style={{ color: item.status.includes("Completed") ? T.green : T.red, fontSize: "14px" }}>{item.status}</span></Typography>
                                    <Typography sx={{ fontSize: 14, color: T.brownLt, lineHeight: 1.7 }}>{item.desc}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// ─── HOW IT WORKS ─────────────────────────────────────────────────────────────
const HowItWorks = () => {
    const steps = [
        { icon: "📍", num: "01", title: "Find Your Zone", desc: "We check if you're within one of our active 2 km delivery zones.", color: T.red },
        { icon: "📋", num: "02", title: "Pick Today's Meal", desc: "Browse the fresh daily menu — Thalis, Biryani, Roti-Sabji, Dal-Rice.", color: "#E65100" },
        { icon: "🍳", num: "03", title: "Zone Kitchen Cooks", desc: "Your order goes straight to the nearest dedicated zone kitchen.", color: T.gold },
        { icon: "🛵", num: "04", title: "Delivered Fast", desc: "Your rider picks up and delivers within the zone. Hot and fresh.", color: T.green },
    ];

    return (
        <Box component="section" id="how-it-works" sx={{ scrollMarginTop: "60px", padding: "40px 5%", background: "#FFF8E1" }}>
            <Box sx={{ maxWidth: 1100, margin: "0 auto" }}>
                <Box sx={{ textAlign: "center", marginBottom: { xs: "48px", md: "64px" } }}>
                    <Typography sx={{ fontSize: { xs: 11, md: 13 }, letterSpacing: ".1em", textTransform: "uppercase", color: T.red, fontWeight: 700, marginBottom: "12px" }}>— How It Works</Typography>
                    <Typography component="h2" className="dp" sx={{ fontSize: "clamp(1.8rem,3.5vw,3.5rem)", fontWeight: 900, color: "#4a342e", lineHeight: 1.1 }}>
                        Simple. Local. <Box component="em" sx={{ color: T.red, fontStyle: "italic" }}>Delicious.</Box>
                    </Typography>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: "24px", marginBottom: { xs: "50px", md: "60px" } }}>
                    {steps.map(s => (
                        <Box key={s.num} sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "16px", padding: "26px", boxShadow: `0 4px 16px ${T.shadow}`, position: "relative" }}>
                            <Box sx={{ position: "absolute", top: 16, right: 20, fontSize: 44, fontWeight: 900, color: `${s.color}12`, fontFamily: "'Playfair Display',serif", lineHeight: 1 }}>{s.num}</Box>
                            <Box sx={{ width: 48, height: 48, borderRadius: "12px", background: `${s.color}18`, border: `1px solid ${s.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, marginBottom: "16px" }}>{s.icon}</Box>
                            <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, color: "#4a342e", marginBottom: "8px" }}>{s.title}</Typography>
                            <Typography sx={{ fontSize: 14, color: T.brownLt, lineHeight: 1.7 }}>{s.desc}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Animated delivery path - Scaled to Medium Size */}
                <Box sx={{
                    background: T.card, borderRadius: "20px", padding: { xs: "24px", md: "32px 40px" },
                    border: `1px solid ${T.border}`, boxShadow: `0 6px 20px ${T.shadow}`,
                }}>
                    <Box sx={{ textAlign: "center", marginBottom: { xs: "16px", md: "24px" } }}>
                        <Typography component="span" sx={{ fontSize: { xs: 13, md: 15 }, fontWeight: 700, color: "#4a342e" }}>🔴 Live: A delivery is on the way in New Town Zone</Typography>
                    </Box>
                    <Box sx={{ position: "relative", height: { xs: 48, md: 60 }, background: "#FFF8E1", borderRadius: "12px", overflow: "hidden", border: `1px dashed ${T.border}` }}>
                        {[0, 20, 40, 60, 80, 100].map(p => (
                            <Box key={p} sx={{
                                position: "absolute", left: `${p}%`, top: "50%", transform: "translateY(-50%)",
                                width: { xs: 6, md: 10 }, height: { xs: 6, md: 10 }, borderRadius: "50%",
                                background: p <= 70 ? T.gold : T.border,
                                boxShadow: p === 0 || p === 100 ? `0 0 0 4px ${T.gold}33` : "none",
                            }} />
                        ))}
                        <Typography component="span" sx={{ position: "absolute", left: 0, bottom: { xs: 4, md: 6 }, fontSize: { xs: 10, md: 12 }, color: T.brownLt, paddingLeft: "8px" }}>Kitchen 🍳</Typography>
                        <Typography component="span" sx={{ position: "absolute", right: 0, bottom: { xs: 4, md: 6 }, fontSize: { xs: 10, md: 12 }, color: T.brownLt, paddingRight: "8px" }}>Your Door 🏠</Typography>
                        <Box sx={{
                            position: "absolute",
                            top: "50%",
                            transform: "translateY(-50%) scaleX(-1)",
                            fontSize: { xs: 20, md: 28 },
                            animation: "scooterRun 4s ease-in-out infinite",
                        }}>🛵</Box>
                    </Box>

                    {/* Updated Status Labels */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: { xs: "12px", md: "16px" },
                        flexWrap: { xs: "nowrap", md: "wrap" },
                        gap: { xs: "4px", md: "8px" }
                    }}>
                        {[
                            { top: "Order", bottom: "Placed✅", full: "Order Placed ✅" },
                            { top: "Kitchen", bottom: "Cooking🔥", full: "Kitchen Cooking 🔥" },
                            { top: "On the", bottom: "Way🛵", full: "On the Way 🛵" },
                            { top: "Arriving", bottom: "Soon⏳", full: "Arriving Soon ⏳" }
                        ].map((status, i) => (
                            <Typography component="div" key={status.full} sx={{
                                fontSize: { xs: 10, md: 14 },
                                fontWeight: 600,
                                color: i < 3 ? "#4a342e" : T.brownLt,
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" }, // Stacks on mobile, inline on desktop
                                alignItems: "center",
                                textAlign: "center",
                                gap: { xs: "2px", md: "6px" },
                                flex: { xs: 1, md: "auto" } // Ensures equal 25% column spacing on mobile
                            }}>
                                {/* Mobile View: Stacked words */}
                                <Box component="span" sx={{ display: { xs: "block", md: "none" } }}>{status.top}</Box>
                                <Box component="span" sx={{ display: { xs: "block", md: "none" } }}>{status.bottom}</Box>

                                {/* Laptop/Tap View: Full string unchanged */}
                                <Box component="span" sx={{ display: { xs: "none", md: "block" } }}>{status.full}</Box>
                            </Typography>
                        ))}
                    </Box>

                </Box>
            </Box>
        </Box>
    );
};

// ─── ZONE MAP SECTION ─────────────────────────────────────────────────────────
const Zones = () => {
    const [activeZone, setActiveZone] = useState(0);

    // Updated zones array: Only New Town is active
    const zones = [
        { name: "New Town Zone", area: "New Town, Biswa Bangla Gate", active: true, kitchens: 2, riders: 8, coverage: "Biswa Bangla Gate, Axis Mall, Aquatica Banquet, Biswa Bangla Convocation Center, Thakdari, Candor Techspace, Newtown Bus Stand, Unitech Gate 1, Bolaka Abasan.", color: T.red, icon: "🏙️" },
        { name: "Salt Lake Zone", area: "Salt Lake, Bidhan Nagar", active: false, coverage: "Coming Soon", color: T.brownLt, icon: "🌉" },
        { name: "Jadavpur Zone", area: "Jadavpur, Kolkata", active: false, coverage: "Coming Soon", color: T.brownLt, icon: "🌆" },
        { name: "Dum Dum Zone", area: "Dum Dum, Jessore Road", active: false, coverage: "Coming Soon", color: T.brownLt, icon: "🏘️" },
        { name: "Garia Zone", area: "Garia, Narendrapur", active: false, coverage: "Coming Soon", color: T.brownLt, icon: "🗺️" },
    ];
    const z = zones[activeZone];

    return (
        <Box component="section" id="zones" sx={{ scrollMarginTop: "20px", padding: "60px 5%", background: "#FFF8E1" }}>
            <Box sx={{ maxWidth: 1100, margin: "0 auto" }}>
                <Box sx={{ textAlign: "center", marginBottom: { xs: "40px", md: "60px" } }}>
                    <Typography sx={{ fontSize: { xs: 11, md: 13 }, letterSpacing: ".1em", textTransform: "uppercase", color: T.red, fontWeight: 700, marginBottom: "12px" }}>— Service Zones</Typography>
                    <Typography component="h2" className="dp" sx={{ fontSize: "clamp(1.8rem,3.5vw,3.5rem)", fontWeight: 900, color: "#4a342e", lineHeight: 1.1 }}>
                        2 km Radius from our kitchen.<br /><Box component="em" sx={{ color: T.red, fontStyle: "italic" }}>Always Hyper-Local.</Box>
                    </Typography>
                </Box>

                <Box className="two-col" sx={{ display: "flex", gap: "5%", alignItems: "flex-start", flexWrap: "wrap" }}>
                    <Box sx={{ flex: "0 0 35%", minWidth: 220 }}>
                        <Typography sx={{ marginBottom: "12px", fontSize: 14, fontWeight: 700, color: "#4a342e", textTransform: "uppercase" }}>Select a Zone</Typography>
                        {zones.map((zone, i) => (
                            <Box key={zone.name} onClick={() => setActiveZone(i)} sx={{
                                background: activeZone === i ? `${zone.color}12` : T.card, border: `1px solid ${activeZone === i ? zone.color : T.border}`,
                                borderRadius: "12px", padding: "16px", marginBottom: "10px", cursor: "pointer", transition: "all .2s",
                                boxShadow: activeZone === i ? `0 4px 12px ${zone.color}22` : "none",
                            }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
                                    <Box component="span" sx={{ fontSize: 24 }}>{zone.icon}</Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#4a342e" }}>{zone.name}</Typography>
                                        <Typography sx={{ fontSize: 12, color: T.brownLt }}>{zone.area}</Typography>
                                    </Box>
                                    <Box sx={{ padding: "4px 10px", borderRadius: "16px", background: zone.active ? `rgba(46,125,50,.12)` : `rgba(109,76,65,.08)`, fontSize: 10, fontWeight: 700, color: zone.active ? T.green : T.brownLt, textTransform: "uppercase" }}>
                                        {zone.active ? "● Live" : "◌ Soon"}
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 240 }}>
                        <Box sx={{ background: T.card, borderRadius: "20px", padding: "32px", border: `1px solid ${T.border}`, boxShadow: `0 6px 20px ${T.shadow}`, marginBottom: "20px", textAlign: "center" }}>
                            <Box sx={{ position: "relative", width: 200, height: 200, margin: "0 auto 16px" }}>
                                <svg viewBox="0 0 220 220" style={{ width: "100%", height: "100%" }}>
                                    <circle cx="110" cy="110" r="100" fill="none" stroke={T.border} strokeWidth="1" />
                                    <circle cx="110" cy="110" r="70" fill="none" stroke={T.border} strokeWidth="1" />
                                    <circle cx="110" cy="110" r="90" fill={`${z.color}10`} stroke={z.color} strokeWidth="2" strokeDasharray="6 3" />
                                    <circle cx="110" cy="110" r="10" fill={z.color} opacity=".9" />
                                    <text x="110" y="114" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">🍳</text>
                                    {z.active && [{ cx: 148, cy: 86 }, { cx: 82, cy: 140 }, { cx: 155, cy: 138 }].map((dot, i) => (
                                        <g key={i}>
                                            <circle cx={dot.cx} cy={dot.cy} r="7" fill="#FFC107" opacity=".9" />
                                            <text x={dot.cx} y={dot.cy + 3} textAnchor="middle" fill={"#4a342e"} fontSize="8">🛵</text>
                                        </g>
                                    ))}
                                </svg>
                            </Box>
                            <Typography sx={{ fontSize: 14, color: T.brownLt, marginBottom: "6px" }}>Newtown Action Area 1B, 1C, 1D:  {z.coverage}</Typography>
                            <Typography className="dp" sx={{ fontSize: 22, fontWeight: 700, color: T.brown }}>{z.name}</Typography>
                        </Box>

                        {z.active ? (
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                                {[{ label: "Kitchens", val: z.kitchens, icon: "🍳", color: T.red }, { label: "Active Riders", val: z.riders, icon: "🛵", color: "#E65100" }].map(s => (
                                    <Box key={s.label} sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "16px", textAlign: "center", boxShadow: `0 3px 10px ${T.shadow}` }}>
                                        <Box sx={{ fontSize: 24, marginBottom: "4px" }}>{s.icon}</Box>
                                        <Typography className="dp" sx={{ fontSize: 26, fontWeight: 900, color: s.color }}>{s.val}</Typography>
                                        <Typography sx={{ fontSize: 12, color: T.brownLt }}>{s.label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "16px", padding: "32px", textAlign: "center" }}>
                                <Box sx={{ fontSize: 44, marginBottom: "12px" }}>📬</Box>
                                <Typography component="h3" className="dp" sx={{ fontSize: 20, fontWeight: 700, color: "#4a342e", marginBottom: "8px" }}>Coming Soon!</Typography>
                                <Typography sx={{ fontSize: 14, color: T.brownLt, marginBottom: "16px" }}>Register your interest.</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// ─── TEAM SECTION ─────────────────────────────────────────────────────────────
/*
const Team = () => {
    const members = [
        {
            name: "Subham Kundu",
            role: "Founder & CEO",
            image: subhamPhoto,
            color: "#E65100",
            bio: "Ensuring your late-night cravings hit your doorstep before the movie gets to the good part.",
            xLink: "https://x.com/subham", // <-- Add actual link here
            linkedinLink: "https://www.linkedin.com/in/subham-kundu-02021a248/" // <-- Add actual link here
        },
        {
            name: "Arnab Kapri",
            role: "Co-Founder & COO ",
            image: arnabPhoto,
            color: T.red,
            bio: "Why pay more when the food costs less but the same quality.",
            xLink: "https://x.com/arnab", // <-- Add actual link here
            linkedinLink: "https://www.linkedin.com/in/arnab-kapri-4555a7227/" // <-- Add actual link here
        },
        {
            name: "Subhajit Mondal",
            role: "Co-Founder & CFO",
            image: subhajitPhoto,
            color: T.red,
            bio: "Experimenting with flavors that'll make you rethink.",
            xLink: "https://x.com/subhajit", // <-- Add actual link here
            linkedinLink: "https://www.linkedin.com/in/subhajit-mondal-a5597b223/" // <-- Add actual link here
        },
    ];

    return (
        <Box component="section" id="team" sx={{ scrollMarginTop: "10px", padding: "70px 5%", background: "#FFF8E1" }}>
            <Box sx={{ maxWidth: 1100, margin: "0 auto" }}>
                <Box sx={{ textAlign: "center", marginBottom: "40px" }}>
                    <Typography sx={{ fontSize: 13, letterSpacing: ".1em", textTransform: "uppercase", color: T.red, fontWeight: 700, marginBottom: "12px" }}>— The Team</Typography>
                    <Typography component="h2" className="dp" sx={{ fontSize: "clamp(1.8rem,3.5vw,3.5rem)", fontWeight: 900, color: "#4a342e" }}>
                        The People Who<br /><Box component="em" sx={{ color: T.red, fontStyle: "italic" }}>Cook Up the Future</Box>
                    </Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: "28px" }}>
                    {members.map((m, i) => (
                        <Box key={m.name} sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: "18px", padding: "32px 20px", textAlign: "center", boxShadow: `0 4px 16px ${T.shadow}`, transition: "transform .3s", position: "relative", overflow: "hidden", animation: `fadeUp .5s ${i * .1}s ease both`, '&:hover': { transform: "translateY(-5px)" } }}>
                            <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg,transparent,${m.color},transparent)` }} />
                            <Box sx={{ position: "relative", display: "inline-block", marginBottom: "24px" }}>
                                <Box sx={{ width: 150, height: 150, borderRadius: "50%", background: `radial-gradient(circle,${m.color}22,${m.color}08)`, border: `2px solid ${m.color}55`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", overflow: "hidden" }}>
                                    <Box component="img" src={m.image} alt={m.name} sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center" }} />
                                </Box>
                                <Box sx={{ position: "absolute", inset: -8, borderRadius: "50%", border: `1px solid ${m.color}33`, animation: "pulseRing 2.5s ease-out infinite" }} />
                            </Box>
                            <Typography component="h3" sx={{ fontSize: 20, fontWeight: 700, color: "#4a342e", marginBottom: "6px" }}>{m.name}</Typography>
                            <Typography sx={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: m.color, fontWeight: 700, marginBottom: "14px" }}>{m.role}</Typography>
                            <Typography sx={{ fontSize: 14, color: T.brownLt, lineHeight: 1.7, marginBottom: "20px" }}>{m.bio}</Typography>

                            <Box sx={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                                {m.linkedinLink && (
                                    <Box component="a" href={m.linkedinLink} target="_blank" rel="noopener noreferrer" sx={{ width: 32, height: 32, borderRadius: "50%", background: T.bg, border: `1px solid ${T.border}`, color: T.brownLt, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", transition: "all .2s", '&:hover': { background: m.color, color: "#fff", borderColor: m.color } }}>
                                        <LinkedInIcon sx={{ fontSize: 18 }} />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};
*/

// ─── CONTACT ──────────────────────────────────────────────────────────────────
const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", zone: "", type: "customer", message: "" });
    const [sent, setSent] = useState(false);
    const [focused, setFocused] = useState("");
    const [errors, setErrors] = useState({});

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Required";
        if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
        if (!form.message.trim()) e.message = "Required";
        return e;
    };
    const [loading, setLoading] = useState(false);
    const handleSubmit = async () => {

        const errs = validate();

        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }

        setLoading(true);

        try {

            const frontend_ip = await fetchIP();

            await sendContactMessage({
                name: form.name,
                email: form.email,
                phone: form.phone,
                zone: form.zone,
                type: form.type,
                message: form.message,
                frontend_ip,
            });

            setErrors({});
            setSent(true);

            setForm({
                name: "",
                email: "",
                phone: "",
                zone: "",
                type: "customer",
                message: "",
            });

            setTimeout(() => setSent(false), 5000);

        } catch (error) {

            console.error(error);

            alert(
                error?.response?.data?.detail ||
                "Unable to send your message. Please try again."
            );

        } finally {

            setLoading(false);

        }
    };

    const inp = (field) => ({
        width: "100%", padding: "12px 14px", background: focused === field ? "#FFFEF5" : T.bg,
        border: `1px solid ${errors[field] ? T.red : focused === field ? T.gold : T.border}`,
        borderRadius: "8px", color: "#4a342e", fontSize: 14, outline: "none", transition: "all .2s", fontFamily: "Inter,sans-serif",
    });

    return (
        <Box component="section" id="contact" sx={{ scrollMarginTop: "50px", padding: "60px 5%", background: T.bg }}>
            <Box sx={{ maxWidth: 1100, margin: "0 auto" }}>
                <Box sx={{ textAlign: "center", marginBottom: "50px" }}>
                    <Typography sx={{ fontSize: 13, letterSpacing: ".1em", textTransform: "uppercase", color: T.red, fontWeight: 700, marginBottom: "12px" }}>— Contact Us</Typography>
                    <Typography component="h2" className="dp" sx={{ fontSize: "clamp(1.8rem,3.5vw,3.5rem)", fontWeight: 900, color: "#4a342e" }}>
                        Reach Out.<br /><Box component="em" sx={{ color: T.red, fontStyle: "italic" }}>We're Just 2 km Away.</Box>
                    </Typography>
                </Box>

                <Box className="two-col" sx={{ display: "flex", gap: "6%", flexWrap: "wrap", alignItems: "flex-start" }}>
                    <Box sx={{ flex: "0 0 35%", minWidth: 240 }}>
                        <Typography sx={{ color: T.brownLt, lineHeight: 1.8, fontSize: 15, marginBottom: "26px" }}>Want to order, partner with us, open a zone kitchen, or join as a delivery rider? We'd love to hear from you.</Typography>
                        {[{ icon: "📍", label: "HQ", val: "New Town, Kolkata" }, { icon: "📞", label: "Helpline", val: "+91 7003057695" }, { icon: "✉️", label: "Email", val: "support@mealno.in" }].map(info => (
                            <Box key={info.label} sx={{ display: "flex", gap: "14px", marginBottom: "20px", alignItems: "flex-start" }}>
                                <Box sx={{ width: 42, height: 42, borderRadius: "10px", background: "rgba(255,215,0,.18)", border: `1px solid ${T.gold}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{info.icon}</Box>
                                <Box>
                                    <Typography sx={{ fontSize: 11, color: T.red, letterSpacing: ".06em", textTransform: "uppercase", fontWeight: 700, marginBottom: "2px" }}>{info.label}</Typography>
                                    <Typography sx={{ fontSize: 15, color: "#4a342e", fontWeight: 500 }}>{info.val}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 280, background: T.card, border: `1px solid ${T.border}`, borderRadius: "18px", boxShadow: `0 6px 24px ${T.shadow}`, marginTop: { xs: "32px", md: "0" } }}>
                        <Box sx={{ padding: "32px" }}>
                            {sent ? (
                                <Box sx={{ textAlign: "center", padding: "40px 0", animation: "bounceIn .5s ease both" }}>
                                    <Box sx={{ fontSize: 64, marginBottom: "16px" }}>🎉</Box>
                                    <Typography component="h3" className="dp" sx={{ fontSize: 26, color: "#4a342e", marginBottom: "8px" }}>Got it!</Typography>
                                    <Typography sx={{ color: T.brownLt, fontSize: 15 }}>Our team responds within a few hours. Stay hungry! 🍛</Typography>
                                </Box>
                            ) : (
                                <>
                                    <Typography component="h3" sx={{ fontSize: 22, fontWeight: 700, color: "#4a342e", marginBottom: "4px" }}>Get In Touch</Typography>
                                    <Typography sx={{ fontSize: 14, color: T.brownLt, marginBottom: "20px" }}>Fill in your details and we'll get back to you fast.</Typography>

                                    <Box className="form-2col" sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>
                                        <Box>
                                            <Typography component="label" sx={{ fontSize: 12, color: T.brownLt, fontWeight: 700, marginBottom: "4px", display: "block" }}>Name *</Typography>
                                            <Box component="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} onFocus={() => setFocused("name")} onBlur={() => setFocused("")} placeholder="Your name" sx={inp("name")} />
                                        </Box>
                                        <Box>
                                            <Typography component="label" sx={{ fontSize: 12, color: T.brownLt, fontWeight: 700, marginBottom: "4px", display: "block" }}>Email *</Typography>
                                            <Box component="input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} onFocus={() => setFocused("email")} onBlur={() => setFocused("")} placeholder="you@email.com" sx={inp("email")} />
                                        </Box>
                                    </Box>
                                    <Box sx={{ marginBottom: "20px" }}>
                                        <Typography component="label" sx={{ fontSize: 12, color: T.brownLt, fontWeight: 700, marginBottom: "4px", display: "block" }}>Message *</Typography>
                                        <Box component="textarea" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} onFocus={() => setFocused("message")} onBlur={() => setFocused("")} placeholder="Tell us how we can help..." rows={4} sx={{ ...inp("message"), resize: "vertical" }} />
                                    </Box>
                                    <Box
                                        component="button"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        sx={{
                                            width: "100%",
                                            padding: "14px",
                                            background: loading ? "#e0e0e0" : T.gradBtn,
                                            color: "#4a342e",
                                            fontWeight: 800,
                                            fontSize: 15,
                                            border: "none",
                                            borderRadius: "10px",
                                            cursor: loading ? "not-allowed" : "pointer",
                                            transition: "all .2s",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "10px",
                                            opacity: loading ? 0.85 : 1,
                                            "&:hover": {
                                                transform: loading ? "none" : "translateY(-2px)",
                                            },
                                        }}
                                    >
                                        {loading ? (
                                            <>
                                                <Box
                                                    sx={{
                                                        width: 18,
                                                        height: 18,
                                                        border: "2px solid rgba(109,76,65,.25)",
                                                        borderTop: `2px solid #4a342e`,
                                                        borderRadius: "50%",
                                                        animation: "rotateSlow .8s linear infinite",
                                                    }}
                                                />
                                                Sending...
                                            </>
                                        ) : (
                                            "Send Message →"
                                        )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// // ─── FOOTER ───────────────────────────────────────────────────────────────────
// ✅ ADDED onReviewClick prop and Contact Info section
const Footer = ({ onReviewClick }) => {
    const cols = [
        {
            title: "Mealno",
            links: [
                { l: "About Us", h: "#about" },
                { l: "How It Works", h: "#how-it-works" },
                { l: "Careers", h: "#contact" }
            ]
        },
        {
            title: "Policies",
            links: [
                { l: "Terms of Service", h: "https://terms.mealno.in/#/terms" },
                { l: "Privacy Policy", h: "https://terms.mealno.in/#/privacy" },
                { l: "Refund Policy", h: "https://terms.mealno.in/#/refund" },
                { l: "Delivery Policy", h: "https://terms.mealno.in/#/delivery" },
                { l: "Walet Terms & Conditions", h: "https://terms.mealno.in/#/wallet" },
                { l: "Coupon and Promotional Offer Terms", h: "https://terms.mealno.in/#/coupons" },
                { l: "Mealno Referral Program Terms", h: "https://terms.mealno.in/#/referral" },
                { l: "Community Guidelines", h: "https://terms.mealno.in/#/community" },
                { l: "Grievance Redressal Policy", h: "https://terms.mealno.in/#/grievance" },
            ]
        },
    ];

    const socials = [
        { icon: <FacebookIcon fontSize="inherit" />, href: "https://www.facebook.com/share/19EkhbJL29/", label: "Facebook" },
        { icon: <InstagramIcon fontSize="inherit" />, href: "https://www.instagram.com/mealno.in", label: "Instagram" },
        { icon: <LinkedInIcon fontSize="inherit" />, href: "https://www.linkedin.com/company/mealno.in/", label: "LinkedIn" },
        { icon: <WhatsAppIcon fontSize="inherit" />, href: "https://wa.me/917003057695", label: "WhatsApp" },
    ];

    return (
        <Box component="footer" id="policies" sx={{ backgroundColor: "#4a342e", color: "#FDEBD0" }}>
            <Box className="footer-inner" sx={{ maxWidth: 1100, margin: "0 auto", padding: "50px 5%", display: "flex", gap: "40px", flexWrap: "wrap", justifyContent: "space-between" }}>
                <Box sx={{ flex: "0 0 260px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <Box component="img" src={iconlogo} alt="Mealno icon Logo"
                            sx={{
                                height: { xs: 90, md: 90 }, width: "auto", objectFit: "contain",
                            }}
                        />
                    </Box>
                    <Typography sx={{ fontSize: 13, color: "#BCAAA4", lineHeight: 1.7, marginBottom: "20px" }}>
                        Zone-based daily meal delivery for students, workers & families. Real food, real prices.
                    </Typography>

                    <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "24px" }}>
                        {socials.map(s => (
                            <Box
                                component="a"
                                key={s.label}
                                href={s.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={s.label}
                                sx={{
                                    width: 34, height: 34, borderRadius: "50%",
                                    background: "rgba(255,255,255,.08)",
                                    border: "1px solid rgba(255,255,255,.14)",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 15, color: "#FDEBD0", textDecoration: "none",
                                    transition: "all .2s",
                                    '&:hover': { background: T.gold, color: "#4a342e", transform: "translateY(-3px)", borderColor: T.gold }
                                }}
                            >
                                {s.icon}
                            </Box>
                        ))}
                    </Box>

                    {/* Give Review Button */}
                    <Box
                        component="button"
                        onClick={onReviewClick}
                        sx={{
                            padding: "10px 20px",
                            borderRadius: "20px",
                            background: T.gradBtn,
                            color: "#4a342e",
                            border: "none",
                            fontWeight: 800,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "14px",
                            transition: "all .2s",
                            '&:hover': { transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(255,215,0,0.3)" }
                        }}
                    >
                        ⭐ Review Us
                    </Box>
                </Box>

                {cols.map(col => (
                    <Box key={col.title} sx={{ minWidth: 120 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.gold, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "16px" }}>{col.title}</Typography>
                        {col.links.map(lk => (
                            <Box
                                component="a"
                                key={lk.l}
                                href={lk.h}
                                target={lk.h.startsWith("http") ? "_blank" : "_self"}
                                rel={lk.h.startsWith("http") ? "noopener noreferrer" : ""}
                                sx={{ display: "block", fontSize: 14, color: "#BCAAA4", textDecoration: "none", marginBottom: "10px", transition: "color .2s", '&:hover': { color: T.gold } }}
                            >
                                {lk.l}
                            </Box>
                        ))}
                    </Box>
                ))}

                {/* 📞 Contact Info Column */}
                <Box sx={{ minWidth: 160 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.gold, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "16px" }}>
                        Contact
                    </Typography>

                    {/* Helpline */}
                    <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "14px" }}>
                        <Box component="span" sx={{ fontSize: 16 }}>📞</Box>
                        <Box>
                            <Typography sx={{ fontSize: 11, color: "#BCAAA4", textTransform: "uppercase", fontWeight: 700, letterSpacing: ".04em" }}>
                                Helpline
                            </Typography>
                            <Box
                                component="a"
                                href="tel:+917003057695"
                                sx={{ display: "inline-block", fontSize: 14, color: "#FDEBD0", textDecoration: "none", transition: "color .2s", '&:hover': { color: T.gold } }}
                            >
                                +91 7003057695
                            </Box>
                        </Box>
                    </Box>

                    {/* Email */}
                    <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                        <Box component="span" sx={{ fontSize: 16 }}>✉️</Box>
                        <Box>
                            <Typography sx={{ fontSize: 11, color: "#BCAAA4", textTransform: "uppercase", fontWeight: 700, letterSpacing: ".04em" }}>
                                Email
                            </Typography>
                            <Box
                                component="a"
                                href="mailto:support@mealno.in"
                                sx={{ display: "inline-block", fontSize: 14, color: "#FDEBD0", textDecoration: "none", transition: "color .2s", '&:hover': { color: T.gold } }}
                            >
                                support@mealno.in
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ borderTop: "1px solid rgba(255,255,255,.08)", padding: "20px 5%" }}>
                <Box sx={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "8px" }}>
                    <Typography component="span" sx={{ fontSize: 12, color: "#BCAAA4" }}>© 2026 Mealno · Made with in Kolkata, India.</Typography>
                </Box>
            </Box>
        </Box>
    );
};
// ─── MAIN LANDING COMPONENT ───────────────────────────────────────────────────
// ✅ RENAMED to MealnoLanding so it works perfectly with your App.js routing
export default function MealnoLanding() {
    const [activeSection, setActiveSection] = useState("home");
    const [showOrderPopup, setShowOrderPopup] = useState(false);

    // ✅ ADDED React Router navigation hook
    const navigate = useNavigate();

    useEffect(() => {
        // Removed "team" from observer ids list
        const ids = ["home", "how-it-works", "menu", "zones", "about", /* "team", */ "contact", "policies"];
        const observer = new IntersectionObserver(
            entries => { entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
            { threshold: 0.3 }
        );
        ids.forEach(id => { const el = document.getElementById(id); if (el) observer.observe(el); });
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <GlobalStyles />
            <Nav active={activeSection} setShowOrderPopup={setShowOrderPopup} />
            <Ticker />
            <Home setShowOrderPopup={setShowOrderPopup} />
            <About />
            <HowItWorks />
            <Zones />
            {/* <Team /> */}
            <Contact />

            {/* ✅ PASSED onReviewClick prop to handle navigation */}
            <Footer
                onReviewClick={() => {
                    window.scrollTo(0, 0); // Scroll to top before navigating
                    navigate("/review");   // Redirects to the separate review page
                }}
            />

            {/* ✅ FLOATING WHATSAPP BUTTON */}
            <Box
                component="a"
                href="https://wa.me/917003057695"
                target="_blank"
                rel="noopener noreferrer"
                title="Chat with us on WhatsApp"
                sx={{
                    position: "fixed",
                    bottom: { xs: 20, md: 28 },
                    right: { xs: 20, md: 28 },
                    zIndex: 998,
                    width: { xs: 40, md: 40 },
                    height: { xs: 40, md: 40 },
                    borderRadius: "50%",
                    backgroundColor: "#25D366",
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    // boxShadow: "0 4px 18px rgba(37, 211, 102, 0.45)",
                    textDecoration: "none",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                        transform: "scale(1.1)",
                        boxShadow: "0 6px 22px rgba(37, 211, 102, 0.65)",
                        backgroundColor: "#20ba59",
                    },
                }}
            >
                <WhatsAppIcon sx={{ fontSize: { xs: 30, md: 36 } }} />
            </Box>

            {/* ─── ORDER POPUP MODAL ─── */}
            {showOrderPopup && (
                <Box sx={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.4)", // Slightly lighter overlay to enhance the glass effect
                    zIndex: 9999,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "20px",
                    backdropFilter: "blur(6px)", // Blurs the background behind the entire modal layer
                    WebkitBackdropFilter: "blur(6px)"
                }}>
                    <Box sx={{
                        // --- GLASSMORPHISM STYLES ---
                        background: "rgba(255,255,224,.85)", // Semi-transparent whidownloadte
                        backdropFilter: "blur(24px)", // Frosted glass blur
                        WebkitBackdropFilter: "blur(24px)",
                        border: "1px solid rgba(255, 255, 255, 0.9)", // White sheen on the edges
                        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)", // Soft shadow
                        // -----------------------------
                        borderRadius: "24px", padding: { xs: "32px 24px", md: "40px 32px" },
                        maxWidth: "400px", width: "100%", textAlign: "center", position: "relative",
                    }}>
                        {/* Close Button */}
                        <Box
                            component="button"
                            onClick={() => setShowOrderPopup(false)}
                            sx={{
                                position: "absolute", top: "5px", right: "6px",
                                background: "rgba(255, 255, 255, 0.5)",
                                border: "1px solid rgba(255, 255, 255, 0.8)",
                                borderRadius: "50%",
                                width: "32px", height: "32px", fontSize: "14px",
                                fontWeight: 800, fontFamily: "inherit",
                                cursor: "pointer", color: "#4a342e", display: "flex",
                                alignItems: "center", justifyContent: "center", transition: "0.2s",
                                '&:hover': { background: "rgba(255, 255, 255, 0.9)" }
                            }}
                        >
                            X
                        </Box>

                        <Typography component="h3" sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 900, color: "#4a342e", marginBottom: "8px", lineHeight: 1.2 }}>
                            How would you like to order?
                        </Typography>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                            <Box component="a" href="https://user.mealno.in/?install=true" sx={{
                                textDecoration: "none", padding: "16px 24px", borderRadius: "16px",
                                background: T.red, color: "#fff", fontWeight: 700, fontSize: "16px",
                                boxShadow: "0 4px 12px rgba(230,81,0,0.3)", transition: "transform 0.2s",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                '&:hover': { transform: "translateY(-2px)" }
                            }}>
                                Download Apk
                            </Box>

                            {/* Use Website Link */}
                            <Box component="a" href="https://user.mealno.in" sx={{
                                textDecoration: "none", padding: "16px 24px", borderRadius: "16px",
                                background: T.gradBtn, color: "#4a342e", fontWeight: 700, fontSize: "16px",
                                border: `1px solid rgba(255, 255, 255, 0.8)`, // Helps the button pop against the glass
                                transition: "transform 0.2s",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                '&:hover': { transform: "translateY(-2px)" }
                            }}>
                                Use Website
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </>
    );
}
